<template>
  <v-container id="signature_profiles_root" fluid>
    <!-- DIALOG -->
    <router-view name="dialog" @uploaded="searchSignatureProfiles"></router-view>
    <!-- CONTENT -->
    <v-flex>
      <v-row class="mb-6 px-6" align="center" justify="center">
        <v-col cols="7">
          <span class="text-h5">
            {{ $t('signatureProfiles.title.list') }}
          </span>
        </v-col>

        <v-col class="d-flex flex-row">
          <v-btn id="create_profile" color="primary" class="align-self-end mr-8" to="/signatureProfiles/add">
            <v-icon left>mdi-plus</v-icon>
            {{ $t('signatureProfiles.content.createProfile') }}
          </v-btn>

          <v-text-field
            id="search_field"
            v-model="search"
            :label="$t('dataTable.search')"
            append-icon="mdi-magnify"
            single-line
            hide-details
            clearable
          ></v-text-field>
        </v-col>
      </v-row>

      <v-card class="pa-2">
        <v-data-table
          must-sort
          :search="search"
          :headers="headers"
          :items="signatureProfileList"
          :options.sync="pagination"
          :server-items-length="totalItems"
          :loading="dataTableLoading"
          :loading-text="$t('dataTable.content.loadingPleaseWait')"
          :no-data-text="$t('dataTable.content.noDataAvailable')"
          :footer-props="{
            'items-per-page-text': $t('dataTable.footer.itemsPerPageText'),
            'page-text': $t('dataTable.footer.pageText'),
            'items-per-page-options': [10,25,50],
          }"
          calculate-widths
        >
          <template #[`item.name`]="{item}">
            <long-text :max-char="40" :text="item.name" />
          </template>

          <template #[`item.signaturePreview`]="{ item }">
            <div class="signature-preview-cell">
              <div v-if="item.sourceImageDataUrl" class="signature-img-frame">
                <v-img
                  contain
                  max-height="56"
                  max-width="200"
                  class="previewImg"
                  :src="item.sourceImageDataUrl"
                ></v-img>
              </div>
              <div v-else-if="item.imageLoading" class="signature-img-frame signature-img-frame--loading">
                <v-progress-circular indeterminate size="24" width="2" color="blue-grey lighten-2"></v-progress-circular>
              </div>
              <div v-else class="signature-img-frame signature-img-frame--empty">
                <v-icon color="blue-grey lighten-3" size="22">mdi-image-off-outline</v-icon>
              </div>
            </div>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-row justify="end">
              <!-- edit -->
              <v-btn text class="update-btn elevation-0 text--darken-2 pa-1" :to="'signatureProfiles/' + item.id + '/update'">
                <v-icon>mdi-progress-pencil</v-icon>
                <span>{{ $t('dataTable.content.action.update') }}</span>
              </v-btn>

              <!-- delete -->
              <v-btn text class="elevation-0 text--darken-2 pa-1" @click="deleteSignatureProfile(item.id, $event)">
                <v-icon>mdi-delete</v-icon>
                <span>{{ $t('dataTable.content.action.delete') }}</span>
              </v-btn>
            </v-row>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>
  </v-container>
</template>

<script>
import LongText from '@/components/LongText.vue'
import debounce from 'lodash.debounce'
import { showConfirmationMenu, showToastError, showToastSuccess } from '../../../event-bus'
import signatureProfiles from '../../../services/api/signatureProfiles'
import signatures from '../../../services/api/signatures'

export default {
  name: 'SignatureProfiles',
  components: {
    LongText
  },
  data() {
    return {
      search: null,
      pagination: {
        sortby: ['name']
      },
      signatureProfileList: [],
      totalItems: 0,
      dataTableLoading: null,
      headers: [
        {
          text: this.$t('dataTable.header.signatureProfiles.name'),
          align: 'left',
          value: 'name',
          width: '150px'
        },
        {
          text: this.$t('dataTable.header.signatureProfiles.preview') || 'Aláírás',
          align: 'left',
          value: 'signaturePreview',
          sortable: false,
          width: '240px'
        },
        {
          text: this.$t('dataTable.header.actions'),
          align: 'right',
          value: 'actions',
          sortable: false
        }
      ]
    }
  },

  watch: {
    search() {
      this.dataTableLoading = true
      this.searchSignatureProfilesWatcher()
    },
    pagination: {
      handler() {
        this.searchSignatureProfiles()
      }
    }
  },

  methods: {
    async searchSignatureProfiles() {
      this.dataTableLoading = true

      try {
        const pageRequest = {
          'page-num': this.pagination.page,
          'page-size': this.pagination.itemsPerPage || 10,
          'sort-field': this.pagination.sortBy[0],
          'sort-order': this.pagination.sortDesc[0] ? 'desc' : 'asc'
        }
        const page = await signatureProfiles.listMySignatureProfiles(this.search, pageRequest)
        this.signatureProfileList = page.content
        this.totalItems = page.total

        this.loadAllImages()
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      } finally {
        this.dataTableLoading = false
      }
    },

    searchSignatureProfilesWatcher: debounce(function() {
      this.searchSignatureProfiles()
    }, 500),

    async loadAllImages() {
      const promises = this.signatureProfileList.map((item, idx) =>
        this.loadImageForItem(item, idx)
      )
      await Promise.allSettled(promises)
    },

    async loadImageForItem(item, idx) {
      if (item.sourceImageDataUrl) return

      this.$set(this.signatureProfileList[idx], 'imageLoading', true)

      try {
        const profile = await signatureProfiles.getMySignatureProfile(item.id)
        const req = {
          userImageDataUrl: profile.renderedImageDataUrl,
          subsignerName: null
        }
        const mergedBase64 = await signatures.combineImage(req)

        this.$set(this.signatureProfileList, idx, {
          ...this.signatureProfileList[idx],
          ...profile,
          sourceImageDataUrl: mergedBase64.imageDataUrl,
          imageLoading: false
        })
      } catch {
        this.$set(this.signatureProfileList[idx], 'imageLoading', false)
      }
    },

    async deleteSignatureProfile(id, e) {
      const conf = await showConfirmationMenu({
        title: this.$t('dialog.confirmation.title'),
        posX: e.clientX,
        posY: e.clientY
      })
      if (!conf) {
        return
      }

      try {
        await signatureProfiles.deleteMySignatureProfile(id)
        this.searchSignatureProfiles()
        showToastSuccess(this.$t('toastMessage.succesfullyDeleted'))
      } catch (error) {
        const errorMsg = this.getErrorMessage(error)
        showToastError(errorMsg)
      }
    },

    getText(item) {
      return item.textParams ? item.textParams.text : ''
    }
  }
}
</script>

<style scoped>
.v-btn::before {
  background: transparent;
}

.signature-preview-cell {
  padding: 6px 0;
}

.signature-img-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 200px;
  min-height: 64px;
  border: 1.5px solid #b0bec5;
  border-radius: 6px;
  background-color: #ffffff;
  padding: 6px 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

::v-deep .v-data-table td:has(.signature-preview-cell) {
  text-align: left !important;
}

.signature-img-frame--loading,
.signature-img-frame--empty {
  background-color: #f5f7f8;
  border-style: dashed;
}

.previewImg >>> .v-image__image {
  background-position: top left !important;
}

</style>
